import React, { useMemo, useRef } from 'react';
import '../../App.css';
import '../data-management/data-management.css';
import '../../tools/search-results/search-results.css';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// types
import {
  ComputeServiceType,
  DataItemType,
  RucioLocationsType,
  Site,
  SiteStorageArea,
} from '../../utils/types';
import { Event, EventType, EventTarget, MoveDataToStorage } from '../../utils/events';

// functions
import { ContainerHook, siteName } from '../../utils/functions';
import { getComputeServices } from '../interactive-sessions/interactive-sessions-actions';

// classes
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import { ContextMenu } from '../menus/context-menu';
import Scrollbox from '../../tools/scrollbox';
import SiteIcon from '../../tools/site-icon';
import ComputeSession from '../compute-tiles/compute-session';

// icons
import { CircleAlert, CircleX, Info } from 'lucide-react';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions (redux).
import { fnD_countAvailableFiles } from '../../app/data-actions';
import {
  CONTEXT_SYSTEMS,
  CONTEXT_MENU_DATA_STAGING as CONTEXT_MENU,
  ctx_setContextRow,
  ctx_setContextVisible,
} from '../menus/context-menu-slice';
import {
  ctx_buildContextMenu,
  ctx_calculatePosition,
  ctx_menuDisplayed,
} from '../menus/context-menu-actions';
import { handleEvent } from '../../app-actions';
import { getRucioSites } from '../data-management/file-browser-actions';
import { setUserDropdownMenuDisplayed } from '../../app-slice';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function DataStagingComponent(props: {
  allItems: DataItemType[];
  selectedItems?: DataItemType[];
  catalog: string;
  contextSystem: CONTEXT_SYSTEMS;
  contextMenuRefresh?: any;
  hideItemCount?: boolean; // DEFAULT: false
}) {
  //

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state parameters.
  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  // data
  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);
  const sD_rucioLocations: RucioLocationsType[] = useAppSelector(
    (state) => state.data.rucioLocations,
  );
  const sD_storageAreas: SiteStorageArea[] = useAppSelector(
    (state) => state.data.storageAreas,
  );

  // lifetime.
  const sLifetimeStr: string = useAppSelector((state) => state.fileBrowser.lifetimeStr);
  const sLifetime: number = useAppSelector((state) => state.fileBrowser.lifetime);

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // retrieve the list of sites (in availability order) for the 'all items' and
  // 'selected items' lists.
  const sites: {
    site: Site;
    count: number;
  }[] = getRucioSites({
    allItems: props.allItems,
    selectedItems: props.selectedItems,
    catalog: props.catalog,
  });

  // derive namespace access from Redux state.
  const itemsToStage: DataItemType[] = useMemo(
    () =>
      (props.selectedItems ?? []).length > 0
        ? (props.selectedItems ?? [])
        : props.allItems,
    [props.selectedItems, props.allItems],
  );

  // sticky set of items already confirmed accessible; once an item lands in the
  // accessible bucket it stays there until the user starts a new selection, so
  // the Stage button cannot flicker off while further SSE events arrive.
  const accessibleStickyRef = useRef<Set<string>>(new Set());
  const previousItemsToStageRef = useRef<DataItemType[] | null>(null);
  if (previousItemsToStageRef.current !== itemsToStage) {
    accessibleStickyRef.current = new Set();
    previousItemsToStageRef.current = itemsToStage;
  }

  const {
    accessibleItems,
    inaccessibleItems,
    stillCheckingItems,
  }: {
    accessibleItems: DataItemType[];
    inaccessibleItems: DataItemType[];
    stillCheckingItems: DataItemType[];
  } = useMemo(() => {
    const locationMap: Map<string, RucioLocationsType> = new Map();
    for (const location of sD_rucioLocations) {
      locationMap.set(`${location.namespace}:${location.filename}`, location);
    }

    const accessible: DataItemType[] = [];
    const inaccessible: DataItemType[] = [];
    const stillChecking: DataItemType[] = [];
    const sticky: Set<string> = accessibleStickyRef.current;

    for (const item of itemsToStage) {
      const key = `${item.namespace}:${item.filename}`;
      if (sticky.has(key)) {
        accessible.push(item);
        continue;
      }
      const access = locationMap.get(key)?.userCanAccessNamespace;
      if (access === true) {
        sticky.add(key);
        accessible.push(item);
      } else if (access === false) {
        inaccessible.push(item);
      } else {
        stillChecking.push(item);
      }
    }

    return {
      accessibleItems: accessible,
      inaccessibleItems: inaccessible,
      stillCheckingItems: stillChecking,
    };
  }, [itemsToStage, sD_rucioLocations]);

  //	--------------------------------------------------------------------------
  //
  //	E V E N T   D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Handler button clicks.
  //
  //	------------------------------------------------------------

  const onClickBtnHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // prevent propogation of the onClick event.
    event.stopPropagation();

    // stage the data to a specific site.
    if (button.name === 'stage-to') {
      //
      // if the menu is currently displayed then hide it.
      if (ctx_menuDisplayed({ contextSystem: props.contextSystem }) === true) {
        dispatch(
          ctx_setContextVisible({
            contextSystem: props.contextSystem,
            visible: false,
          }),
        );
      } else {
        //
        // update the text in the main context menu.
        ctx_buildContextMenu({
          contextSystem: props.contextSystem,
          contextMenu: 1 /* SITES menu */,
          parentRowID: -1,
        });

        // check if this position is OK - use the button as a references point.
        ctx_calculatePosition({
          contextSystem: props.contextSystem,
          contextMenu: 1 /* SITES menu */,
          button: button.name,
        });

        dispatch(
          ctx_setContextVisible({
            contextSystem: props.contextSystem,
            contextMenu: 1 /* SITES menu */,
            visible: true,
          }),
        );

        dispatch(
          ctx_setContextRow({
            contextSystem: props.contextSystem,
            row: -1,
          }),
        );

        // close user dropdown menu.
        dispatch(setUserDropdownMenuDisplayed(false));

        // refresh the context menu.
        if (props.contextMenuRefresh !== undefined) {
          dispatch(props.contextMenuRefresh(Date.now()));
        }

        // prevent propogation of the onClick event.
        event.stopPropagation();
        //
      }
      //
    }
    //
  }; // onClickBtnHandler

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T S
  //
  //	--------------------------------------------------------------------------

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    //
    //  Column to contain the file details.
    //
    <Scrollbox
      id="data-management-scrollbox"
      direction="vertical"
      width="350px"
      flex="1 1"
      flexDirection="column"
      padding="10px 10px 10px 10px"
      align="end"
    >
      {props.hideItemCount !== true && (
        <div
          className="text-colour"
          data-important="T"
          style={{ margin: '0px 0px 10px 0px' }}
        >
          {(props.selectedItems ?? []).length === 0
            ? t('{{count}} item(s)', { count: props.allItems.length })
            : t('{{count}} item(s) selected', {
                count: (props.selectedItems ?? []).length,
              })}
        </div>
      )}
      {sG_loggedIn && stillCheckingItems.length > 0 && inaccessibleItems.length === 0 && (
        <div
          className="text-colour"
          data-text-green="T"
          style={{ margin: '0px 0px 30px 0px', textAlign: 'right' }}
        >
          <Info
            width={32}
            height={32}
            strokeWidth={1.5}
            style={{ marginRight: '10px' }}
          />
          {t('Checking permission to stage {{stillChecking}} file(s).....', {
            stillChecking: stillCheckingItems.length,
          })}
        </div>
      )}
      {inaccessibleItems.length > 0 &&
        accessibleItems.length === 0 &&
        stillCheckingItems.length === 0 && (
          <div
            className="text-colour"
            data-text-red="T"
            style={{ margin: '0px 0px 30px 0px', textAlign: 'right' }}
          >
            <CircleX
              width={32}
              height={32}
              strokeWidth={1.5}
              style={{ marginRight: '10px' }}
            />
            {t('You do not have permission to stage any of the selected files')}
          </div>
        )}
      {inaccessibleItems.length > 0 && accessibleItems.length > 0 && (
        <div
          className="text-colour"
          data-text-orange="T"
          style={{ margin: '0px 0px 30px 0px', textAlign: 'right' }}
        >
          <CircleAlert
            width={32}
            height={32}
            strokeWidth={1.5}
            style={{ marginRight: '10px' }}
          />
          {t(
            'You do not have permission to stage {{inaccessible}} of {{total}} file(s)',
            {
              inaccessible: inaccessibleItems.length,
              total: accessibleItems.length + inaccessibleItems.length,
            },
          )}
        </div>
      )}
      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'row',
          margin: '0px 0px 30px 0px',
        }}
      >
        <ToolButton
          key="Stage to"
          name="stage-to"
          onClick={onClickBtnHandler}
          tooltip={t('Stage data items to another site')}
          text={t('Stage to') + '  ▸'}
          disabled={sG_loggedIn !== true || accessibleItems.length === 0}
          type={ToolButtonType.SECONDARY}
        />
      </div>
      {sites.length > 0 && (
        <div
          className="text-colour"
          style={{
            textAlign: 'right',
            marginTop: '10px',
            marginBottom: '10px',
          }}
        >
          The data are located at the following sites:
        </div>
      )}
      {sites

        /*
        // map each site to an icon, a site name, and a summary of the number of available data items.
        */

        .map((site) => (
          <div
            className="text-colour"
            style={{
              flex: '0 0 42px',
              width: '300px',
              display: 'flex',
              flexDirection: 'row',
              textAlign: 'center',
              alignItems: 'center',
              margin: '0px 0px 5px 2px',
            }}
          >
            <div
              style={{
                flex: '1 1',
                textAlign: 'right',
              }}
            >
              {'(' +
                fnD_countAvailableFiles({
                  site: site.site,
                  items:
                    (props.selectedItems ?? []).length === 0
                      ? props.allItems
                      : (props.selectedItems ?? []),
                }) +
                ' / ' +
                ((props.selectedItems ?? []).length === 0
                  ? props.allItems.length.toString()
                  : (props.selectedItems ?? []).length.toString()) +
                ' available)'}
            </div>
            <div
              style={{
                flex: '0 0 105px',
                height: 'auto',
                textAlign: 'right',
                alignItems: 'center',
                fontSize: '10pt',
                userSelect: 'none',
              }}
            >
              {siteName({ site: site.site }).substring(0, 10)}
            </div>
            <div style={{ flex: '0 0 5px' }} />
            <SiteIcon site={site.site} size={32} />
          </div>
        ))}
    </Scrollbox>
  );
} // DataStagingComponent
